import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Label,
    Skeleton,
} from '@ui';
import { useUserStore } from '@/stores/userStore';
import { useGroupStore } from '@/stores/groupStore';
import { useGetGroup, useUpdateGroup } from '@hooks/generated/group';
import { Building2, MapPin, Phone, Clock, Users, Settings, Save, Loader2 } from 'lucide-react';
import { toastMessage } from '@ui/components/Toast';
import type { BranchFormData, GroupData } from '../../types';
import { UseUpdateGroupData } from '@/api';

export function BranchTab({ customerLength }: { customerLength?: number }) {
    const { setError, clearError } = useUserStore();
    const selectedGroup = useGroupStore((state) => state.selectedGroup);
    const setSelectedGroup = useGroupStore((state) => state.setSelectedGroup);
    const groups = useGroupStore((state) => state.groups);
    const setGroups = useGroupStore((state) => state.setGroups);

    const groupId = selectedGroup?.id;

    // Branch data
    const { data: groupData, isLoading: isLoadingGroup, refetch: refetchGroup } = useGetGroup({
        request: { groupId: groupId || 0 },
        options: { enabled: !!groupId },
    });

    // Mutations
    const updateBranchMutation = useUpdateGroup({
        options: {
            onSuccess: async () => {
                clearError();
                toastMessage({ message: '지점 정보가 수정 되었습니다.', type: 'success' });
                // refetch 후 store 업데이트
                const { data: updatedGroupData } = await refetchGroup();
                if (updatedGroupData && groupId) {
                    const updatedGroup = updatedGroupData as GroupData;
                    // useGroupStore의 selectedGroup 업데이트
                    setSelectedGroup({
                        id: groupId,
                        name: updatedGroup.name,
                        description: updatedGroup.description,
                    });
                    // Header의 Group Selector 데이터(groups 배열) 업데이트
                    const updatedGroups = groups.map(g =>
                        g.id === groupId
                            ? { ...g, name: updatedGroup.name, description: updatedGroup.description }
                            : g
                    );
                    setGroups(updatedGroups);
                }
            },
            onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
                const errorMessage = err.response?.data?.message || err.message || '지점 정보 업데이트에 실패했습니다.';
                setError(errorMessage);
                toastMessage({ message: errorMessage, type: 'error' });
            },
        },
    });

    const currentGroup = groupData as UseUpdateGroupData | null;

    // 운영시간 파싱 헬퍼 함수
    const parseBusinessHours = (openTime?: string, closeTime?: string) => {
        const parseTime = (time: string | undefined, defaultHour: string, defaultMinute: string) => {
            if (!time) return { hour: defaultHour, minute: defaultMinute };
            const [hour, minute] = time.split(':');
            return { hour: hour || defaultHour, minute: minute || defaultMinute };
        };
        const open = parseTime(openTime, '09', '00');
        const close = parseTime(closeTime, '18', '00');
        return {
            openTimeHour: open.hour,
            openTimeMinute: open.minute,
            closeTimeHour: close.hour,
            closeTimeMinute: close.minute,
        };
    };

    // Branch form
    const branchForm = useForm<BranchFormData>({
        defaultValues: {
            name: '',
            description: '',
            address: '',
            phone: '',
            openTimeHour: '09',
            openTimeMinute: '00',
            closeTimeHour: '18',
            closeTimeMinute: '00',
        },
    });

    // 지점 데이터 로드 시 폼 업데이트
    useEffect(() => {
        if (currentGroup) {
            const hours = parseBusinessHours(currentGroup.startTime, currentGroup.endTime);
            branchForm.reset({
                name: currentGroup.name || '',
                description: currentGroup.description || '',
                address: currentGroup.location || '',
                phone: currentGroup.contactPhone || '',
                ...hours,
            });
        }
    }, [currentGroup, branchForm]);

    const onSubmitBranch = (data: BranchFormData) => {
        if (!groupId) return;

        const formatTime = (hour: string, minute: string) => {
            if (!hour) return undefined;
            const h = hour.padStart(2, '0');
            const m = (minute || '00').padStart(2, '0');
            return `${h}:${m}`;
        };

        const openTime = formatTime(data.openTimeHour, data.openTimeMinute);
        const closeTime = formatTime(data.closeTimeHour, data.closeTimeMinute);

        updateBranchMutation.mutate({
            groupId,
            groupUpdateRequest: {
                name: data.name,
                description: data.description || undefined,
                location: data.address || undefined,
                contactPhone: data.phone || undefined,
                startTime: openTime,
                endTime: closeTime,
            } as never,
        });
    };

    if (!groupId) {
        return (
            <div className="mx-auto max-w-2xl space-y-6">
                <Card>
                    <CardContent className="py-12 text-center">
                        <Building2 className="mx-auto h-12 w-12 text-neutral-300" />
                        <p className="mt-4 text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">그룹을 먼저 선택해주세요.</p>
                        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-2">상단에서 그룹을 선택하면 지점 정보가 표시됩니다.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isLoadingGroup) {
        return (
            <div className="mx-auto max-w-2xl space-y-6">
                <Card>
                    <CardContent className="p-6 space-y-4">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-orange-500" />
                        지점 개요
                    </CardTitle>
                    <CardDescription>현재 선택된 지점의 정보입니다.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="rounded-lg border p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
                                    <Building2 className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">지점명</p>
                                    <p className="font-semibold text-neutral-800 dark:text-neutral-100">{currentGroup?.name || '-'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-lg border p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                                    <Users className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">회원 수</p>
                                    <p className="font-semibold text-neutral-800 dark:text-neutral-100">{customerLength || 0}명</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <form onSubmit={branchForm.handleSubmit(onSubmitBranch)}>
                    <CardHeader>
                        <CardTitle>지점 정보 수정</CardTitle>
                        <CardDescription>지점 정보를 수정할 수 있습니다.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="branchName">지점명</Label>
                            <div className="relative">
                                <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                                <Input
                                    id="branchName"
                                    type="text"
                                    placeholder="지점명을 입력하세요"
                                    className="pl-10"
                                    {...branchForm.register('name', {
                                        required: '지점명을 입력해주세요.',
                                    })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">지점 설명</Label>
                            <div className="relative">
                                <Settings className="absolute left-3 top-3 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                                <textarea
                                    id="description"
                                    placeholder="지점에 대한 설명을 입력하세요"
                                    className="w-full rounded-md border border-input bg-background px-10 py-2 text-sm min-h-[80px]"
                                    {...branchForm.register('description')}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address">주소</Label>
                            <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                                <Input
                                    id="address"
                                    type="text"
                                    placeholder="주소를 입력하세요"
                                    className="pl-10"
                                    {...branchForm.register('address')}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="branchPhone">대표 전화번호</Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                                <Input
                                    id="branchPhone"
                                    type="tel"
                                    placeholder="02-1234-5678"
                                    className="pl-10"
                                    {...branchForm.register('phone')}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>운영시간</Label>
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
                                    <span className="text-sm text-neutral-600 dark:text-neutral-300 w-16">시작</span>
                                    <select
                                        className="flex h-10 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        {...branchForm.register('openTimeHour')}
                                    >
                                        <option value="">시</option>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <option key={i} value={String(i).padStart(2, '0')}>
                                                {String(i).padStart(2, '0')}시
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-neutral-400 dark:text-neutral-500">:</span>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="59"
                                        placeholder="분"
                                        className="w-20"
                                        {...branchForm.register('openTimeMinute')}
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
                                    <span className="text-sm text-neutral-600 dark:text-neutral-300 w-16">종료</span>
                                    <select
                                        className="flex h-10 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        {...branchForm.register('closeTimeHour')}
                                    >
                                        <option value="">시</option>
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <option key={i} value={String(i).padStart(2, '0')}>
                                                {String(i).padStart(2, '0')}시
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-neutral-400 dark:text-neutral-500">:</span>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="59"
                                        placeholder="분"
                                        className="w-20"
                                        {...branchForm.register('closeTimeMinute')}
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="border-t pt-6">
                        <Button type="submit" disabled={!branchForm.formState.isDirty || updateBranchMutation.isPending}>
                            {updateBranchMutation.isPending ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />저장 중...</>
                            ) : (
                                <><Save className="mr-2 h-4 w-4" />변경사항 저장</>
                            )}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
